import { useIntl } from '@leagueplatform/locales';
import { useDocumentTitle } from '@leagueplatform/web-common';
import React from 'react';
import {
  Box,
  Button,
  HeadingText,
  ParagraphText,
  queryHelpers,
  StackItem,
  StackLayout,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import {
  EditProfilePhoto,
  FullScreenContainer,
  UserProfileEditableForm,
  useUserProfileSave,
  useFormState,
  LoadingSpinner,
  SUB_PRODUCT_AREA_NAMES,
  DETAIL,
} from '@telus-alberta-health-web/shared-module';
import defaultProfileImg from 'assets/default-profile-image.svg';
import {
  PRODUCT_AREA,
  SCREEN_NAMES,
  EVENT_NAME,
  trackAnalyticsEvent,
  usePageViewAnalytics,
  SUB_PRODUCT_AREA,
} from '@leagueplatform/analytics';
import { OnboardingFlowContainer } from 'components/onboarding-flow-container/onboarding-flow-container';
import { usePersonalInformation } from 'hooks/use-personal-information/use-personal-information.hook';
import {
  YEAR_DATE_FORMAT_UTC,
  SECONDARY_CONTENT_MAX_WIDTH,
} from 'common/constants';

export const AccountSetupPage = ({
  accountSetupComplete,
}: {
  accountSetupComplete: (error?: Error) => void;
}) => {
  const { formatMessage, formatDate } = useIntl();

  const isMobile = useMediaQuery(queryHelpers.down('mobileLandscape'));

  useDocumentTitle(formatMessage({ id: 'STR_ACCOUNT_SETUP' }));
  usePageViewAnalytics({
    product_area: PRODUCT_AREA.ONBOARDING,
    screen_name: SCREEN_NAMES.ACCOUNT_SET_UP,
    sub_product_area: SUB_PRODUCT_AREA_NAMES.ACCOUNT_SETUP,
  });

  const { data: personalInformation, isLoading: personalInformationIsLoading } =
    usePersonalInformation();

  const dateOfBirth = personalInformation?.userProfile?.dateOfBirth;
  const email = personalInformation?.email;

  const formFields = [
    {
      id: 'preferredFirstName',
      name: 'preferredFirstName',
      label: formatMessage({ id: 'STR_PREFERRED_NAME' }),
      placeholder: formatMessage({ id: 'STR_ENTER_PREFERRED_NAME' }),
      value: personalInformation?.preferredFirstName,
      editable: true,
    },
    {
      id: 'email',
      name: 'email',
      label: formatMessage({ id: 'STR_EMAIL_ADDRESS' }),
      value: email,
      editable: false,
      required: true,
    },
    {
      id: 'birthYear',
      name: 'birthYear',
      label: formatMessage({ id: 'STR_BIRTH_YEAR' }),
      value: formatDate(dateOfBirth, YEAR_DATE_FORMAT_UTC),
      editable: false,
      required: true,
      type: 'text',
    },
  ];

  const { handleUserProfileSave, isLoading: isUserProfileSaveLoading } =
    useUserProfileSave();
  const formState = useFormState(formFields);

  const { inputValue } = formState;

  if (personalInformationIsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <FullScreenContainer
      css={{
        background: '$surfaceBackgroundSecondary',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          inputValue.optedIntoMarketingCommunications = 'true';
          await handleUserProfileSave(inputValue);

          // Track analytics for continue button click
          trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
            product_area: PRODUCT_AREA.ONBOARDING,
            sub_product_area: SUB_PRODUCT_AREA_NAMES.ACCOUNT_SETUP,
            screen_name: SCREEN_NAMES.ACCOUNT_SET_UP,
            detail: DETAIL.CONTINUE,
          });

          accountSetupComplete();
        }}
      >
        <OnboardingFlowContainer
          css={{
            maxWidth: SECONDARY_CONTENT_MAX_WIDTH,
            width: '100%',
            margin: 'auto',
          }}
          isMobile={isMobile}
        >
          <StackItem css={{ marginBottom: '$oneAndHalf' }}>
            <HeadingText level="2" size="lg" css={{ marginBottom: '$half' }}>
              {formatMessage({ id: 'STR_ADD_YOUR_PROFILE_HEADING' })}
            </HeadingText>
            <ParagraphText size="sm">
              {formatMessage({ id: 'STR_ADD_YOUR_PROFILE_DESCRIPTION' })}
            </ParagraphText>
          </StackItem>

          <StackItem
            horizontalAlignment="center"
            css={{
              marginBottom: '$twoAndHalf',
            }}
          >
            <EditProfilePhoto
              defaultProfileImg={defaultProfileImg}
              alt="User Avatar"
              screenName={SCREEN_NAMES.ACCOUNT_SET_UP}
            />
            <HeadingText size="lg" level="3" css={{ marginTop: '$half' }}>
              {personalInformation.displayName}
            </HeadingText>
          </StackItem>
          <StackLayout horizontalAlignment="stretch">
            <UserProfileEditableForm
              fields={formFields}
              formState={formState}
              handleSave={handleUserProfileSave}
              pageContext={{
                productArea: PRODUCT_AREA.ONBOARDING,
                screenName: SCREEN_NAMES.ACCOUNT_SET_UP,
                subProductArea: SUB_PRODUCT_AREA.ACCOUNT_SET_UP,
              }}
            />
            {!isMobile && (
              <Box
                css={{
                  width: '100%',
                  height: '1px',
                  backgroundColor: '$onSurfaceBorderDefault',
                  marginY: '$one',
                }}
              />
            )}
            <Button
              loading={isUserProfileSaveLoading}
              type="submit"
              css={{
                alignSelf: 'flex-end',
                width: isMobile ? '90%' : '',
                justifyContent: isMobile ? 'center' : '',
                position: isMobile ? 'relative' : '',
                right: isMobile ? '$one' : '',
                marginBlockStart: isMobile ? '$oneAndHalf' : '',
                marginBlockEnd: isMobile ? '$twoAndHalf' : '',
              }}
            >
              {formatMessage({ id: 'STR_CONTINUE' })}
            </Button>
          </StackLayout>
        </OnboardingFlowContainer>
      </form>
    </FullScreenContainer>
  );
};
